import React, { useState, useEffect, useCallback } from 'react';
import { ApiRepos } from '../../../contracts/ContractVars';
import { useAuth } from '../../../auth/AuthProvider';
import { toast } from 'react-toastify';
import './OrderComments.css';

const OrderComments = ({ orderId, refreshTrigger = 0 }) => {
    const { user, isAdmin } = useAuth();
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newComment, setNewComment] = useState('');
    const [isExpanded, setIsExpanded] = useState(true);

    const fetchComments = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${ApiRepos}ordercomments/order/${orderId}`, {
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to fetch comments');
            }

            const data = await response.json();
            setComments(data);
        } catch (err) {
            setError(err.message);
            toast.error(`Error loading comments: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        if (orderId) {
            fetchComments();
        }
    }, [orderId, fetchComments, refreshTrigger]);

    const handleSubmitComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) {
            toast.error('Comment cannot be empty');
            return;
        }

        try {
            const response = await fetch(`${ApiRepos}ordercomments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    orderId,
                    content: newComment
                })
            });

            if (!response.ok) {
                throw new Error('Failed to add comment');
            }

            setNewComment('');
            await fetchComments();
            toast.success('Comment added successfully');
        } catch (err) {
            toast.error(`Error adding comment: ${err.message}`);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            const response = await fetch(`${ApiRepos}ordercomments/${commentId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to delete comment');
            }

            await fetchComments();
            toast.success('Comment deleted successfully');
        } catch (err) {
            toast.error(`Error deleting comment: ${err.message}`);
        }
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const isSystemComment = (content) => content.startsWith('[SYSTEM]');

    const formatSystemComment = (content) => content.replace('[SYSTEM]', '').trim();

    return (
        <div className="comments-container">
            <div
                className="comments-header"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <h3>Comments and Activity Log</h3>
                <div className="comments-toggle">{isExpanded ? 'Collapse' : 'Expand'} ({comments.length})</div>
            </div>

            {isExpanded && (
                <div className="comments-content">
                    <form onSubmit={handleSubmitComment} className="comments-form">
                        <input
                            type="text"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add a comment..."
                            className="comments-input"
                        />
                        <button
                            type="submit"
                            className="comments-submit"
                            disabled={!newComment.trim()}
                        >
                            Post
                        </button>
                    </form>

                    {loading ? (
                        <div className="comments-loading">Loading comments...</div>
                    ) : error ? (
                        <div className="comments-error">{error}</div>
                    ) : comments.length === 0 ? (
                        <div className="comments-empty">No comments yet</div>
                    ) : (
                        <div className="comments-list">
                            {comments.map((comment) => (
                                <div
                                    key={comment.id}
                                    className={`comment-item ${isSystemComment(comment.content) ? 'comment-system' : ''}`}
                                >
                                    <div className="comment-header">
                                        <span className="comment-author">{comment.createdByUserName}</span>
                                        <span className="comment-date">{formatDateTime(comment.createdDate)}</span>
                                    </div>
                                    <div className="comment-content">
                                        {isSystemComment(comment.content)
                                            ? formatSystemComment(comment.content)
                                            : comment.content}
                                    </div>
                                    {(user.id === comment.createdByUserId || isAdmin) && !isSystemComment(comment.content) && (
                                        <div className="comment-actions">
                                            <button
                                                onClick={() => handleDeleteComment(comment.id)}
                                                className="comment-delete"
                                                title="Delete comment"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default OrderComments;
